import { useRewardWallet } from '@shkola/api';
import { Link, useRouterState } from '@tanstack/react-router';
import { Bell, ChevronRight, Menu, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SparkleIcon } from '../design/icons';
import { Kbd } from '../design/ui';
import { BugButton } from './BugReport';
import { CommandPalette } from './CommandPalette';
import { NAV, allSections, sectionOf } from './nav';
import { Logo, useNavLayout } from './Sidebar';
import { useViewContext } from './ViewContext';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** A round glass button of the bar (the bell, the search and the menu on a phone). */
const glassButton =
  'glass relative inline-flex size-10 shrink-0 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-white hover:text-ink';

/** The student's sparks, as the bar's counter (ref. «⚡ 3»): opens the rewards. */
function Sparks({ studentId }: { studentId: string }) {
  const { t } = useTranslation();
  const balance = useRewardWallet(studentId).data?.balance;
  if (balance === undefined) return null;
  return (
    <Link
      to="/rewards"
      title={t('studentHome.stats.sparks')}
      className="glass hidden h-10 items-center gap-1.5 rounded-full px-3.5 text-[14px] font-semibold text-ink transition-colors hover:bg-white sm:inline-flex"
    >
      <SparkleIcon size={16} weight="fill" className="text-tone-violet-vivid" aria-hidden />
      {balance}
    </Link>
  );
}

/**
 * The bar over the plate, on the page's own ground (design v4, 27.10, ref. «Courses»): the logo over
 * the menu, where you are (on the line of the blocks' left edge), the search of sections (⌘K), the
 * student's sparks, «Повідомити про помилку» and the bell (on the line of the blocks' right edge). On a phone — the menu
 * button, the logo, the search and the bell.
 */
export function TopBar({ unread, onMenu }: { unread: number; onMenu: () => void }) {
  const { t } = useTranslation();
  const layout = useNavLayout();
  const { current } = useViewContext();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const section = sectionOf(path, layout);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const Icon = section ? NAV[section].icon : undefined;
  return (
    <header className="flex h-16 shrink-0 items-center gap-3 px-4 md:gap-0 md:px-3">
      <button
        type="button"
        aria-label={t('nav.menu')}
        className={`${glassButton} md:hidden`}
        onClick={onMenu}
      >
        <Menu size={18} strokeWidth={1.8} aria-hidden />
      </button>
      <span className="md:hidden">
        <Logo />
      </span>
      <div className="hidden h-full w-[240px] shrink-0 items-center pl-5 md:flex">
        <Logo />
      </div>
      <nav
        aria-label={t('shell.whereAmI')}
        className="hidden min-w-0 items-center gap-1.5 pl-6 text-sm md:flex"
      >
        <span className="text-muted">{t('common.appName')}</span>
        {/* a page outside the menu (e.g. «Догнати пропущене») — only the school's name, no empty step */}
        {section && (
          <>
            <ChevronRight size={15} className="text-faint" aria-hidden />
            <span className="flex min-w-0 items-center gap-1.5 font-medium text-ink">
              {Icon && <Icon size={16} strokeWidth={1.8} aria-hidden className="shrink-0" />}
              <span className="truncate">{t(`nav.${section}`)}</span>
            </span>
          </>
        )}
      </nav>
      <div className="ml-auto flex items-center gap-2 md:pr-8">
        <button
          type="button"
          onClick={() => setSearching(true)}
          className="glass hidden h-10 w-[300px] items-center gap-2 rounded-full pr-2 pl-4 text-sm text-muted transition-colors hover:bg-white lg:flex"
        >
          <Search size={16} strokeWidth={1.8} aria-hidden />
          <span className="flex-1 text-left">{t('shell.searchPlaceholder')}</span>
          <Kbd>{isMac ? '⌘ K' : 'Ctrl K'}</Kbd>
        </button>
        <button
          type="button"
          aria-label={t('shell.search')}
          onClick={() => setSearching(true)}
          className={`${glassButton} lg:hidden`}
        >
          <Search size={17} strokeWidth={1.8} aria-hidden />
        </button>
        {current?.kind === 'student' && <Sparks studentId={current.child.memberId} />}
        <BugButton className={glassButton} />
        <Link to="/notifications" aria-label={t('nav.notifications')} className={glassButton}>
          <Bell size={17} strokeWidth={1.8} aria-hidden />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-primary px-1 text-[10px] font-bold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Link>
      </div>
      {searching && (
        <CommandPalette sections={allSections(layout)} onClose={() => setSearching(false)} />
      )}
    </header>
  );
}
